import { useCallback, useState } from 'react';
import { toSeconds } from '@yandex-int/ims.opaque-types/lib';

import { usePlayerInstance } from '@/scenarios/legacy-player/lib/contexts/instance';
import {
    DragCallbackParams,
    useDrag as useDragBase,
} from '@/scenarios/legacy-player/components/PlayerSkin/parts/Slider/hooks/useDrag';

import { useDuration } from '../../../hooks/use-duration';

interface Params {
    timelineWidth?: number;
    startPosition?: number;
}

export const useTimelineDrag = ({ timelineWidth = 0, startPosition = 0 }: Params) => {
    const instance = usePlayerInstance();
    const duration = useDuration();
    const [dragPositionOnTimeline, setDragPositionOnTimeline] = useState<number>();
    const endPosition = startPosition + timelineWidth;

    const cropDragPosition = useCallback(
        (pageX: number) => {
            let dragPosition = pageX;

            if (dragPosition >= endPosition) {
                dragPosition = endPosition;
            }

            if (dragPosition <= startPosition) {
                dragPosition = startPosition;
            }

            return dragPosition;
        },
        [endPosition, startPosition]
    );

    const onDrag = useCallback(
        ({ pageX }: DragCallbackParams) => {
            setDragPositionOnTimeline(cropDragPosition(pageX));
        },
        [cropDragPosition]
    );

    const onDragStart = useCallback(({ pageX }: DragCallbackParams) => {
        setDragPositionOnTimeline(pageX);
    }, []);

    const onDragEnd = useCallback(
        ({ pageX }: DragCallbackParams) => {
            if (instance && timelineWidth) {
                const dragPosition = cropDragPosition(pageX);

                instance.seek(
                    toSeconds(
                        duration.getTimeFromPercent((dragPosition - startPosition) / timelineWidth)
                            .streamTime
                    )
                );
            }
        },
        [cropDragPosition, duration, instance, startPosition, timelineWidth]
    );

    const { dragging: isDragged, onMouseDown } = useDragBase({
        onDrag,
        onDragStart,
        onDragEnd,
    });

    return {
        dragPositionOnTimeline,
        isDragged,
        onDragStart: onMouseDown,
    };
};
